// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as failoverDomainActions from 'common/modules/failoverDomain/actions';
import { connect } from 'react-redux';
import {
    RouteComponentProps,
    withRouter,
} from 'react-router';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { Loader } from 'common/components';
import { WithErrorPages } from 'common/components/WithPageNotFound/WithErrorPages';
import { Translate } from '@plesk/ui-library';
import { ICONS } from 'common/constants';
import PageHeader from 'admin/failoverDomain/page/components/PageHeader/PageHeader';
import HAHistoryTable from 'admin/highAvailability/components/HistoryTable/HighAvailabilityHistoryTable';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import { PageContent } from 'admin/failoverDomain/page/containers/Styles';
import FailoverDomainCreateDialog from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/FailoverDomainCreateDialog';

export type FailoverDomainActivitiesProps =
    RouteComponentProps<{ id: string }> &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const FailoverDomainActivities: React.FC<FailoverDomainActivitiesProps> = ({
    failoverDomain,
    isLoading,
    failoverDomainActions: {
        getFailoverDomain,
        toggleActiveState,
    },
    match: { params },
}) => {
    const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
    const failoverDomainId = parseInt(params.id, 10);

    const onMount = () => getFailoverDomain(failoverDomainId);

    const handleToggleActive = (isActive: boolean) => toggleActiveState(failoverDomainId, { is_active: isActive });

    const handleEditDialogClose = () => setIsEditDialogOpen(false);

    const handleEditDialogSaved = () => {
        setIsEditDialogOpen(false);
        getFailoverDomain(failoverDomainId);
    };

    return (
        <WithErrorPages onMount={onMount} deps={[failoverDomainId]}>
            <Loader isLoading={isLoading}>
                <PageContent>
                    <PageHeader
                        failoverDomain={failoverDomain}
                        onToggleActive={handleToggleActive}
                        onSettingsClick={() => setIsEditDialogOpen(true)}
                        isToggling={failoverDomain.is_loading}
                        trailingBreadcrumb={
                            <Translate content="failoverDomain.page.cards.activitiesBreadcrumb" />
                        }
                    />
                    <HAHistoryTable
                        failoverDomainId={failoverDomainId}
                        emptyView={
                            <EmptyView
                                title="highAvailability.activities.list.emptyView.title"
                                icon={ICONS.SCREEN}
                            />
                        }
                    />
                </PageContent>
                {isEditDialogOpen && (
                    <FailoverDomainCreateDialog
                        editItem={failoverDomain}
                        onClose={handleEditDialogClose}
                        onSaved={handleEditDialogSaved}
                    />
                )}
            </Loader>
        </WithErrorPages>
    );
};

const mapStateToProps = (state: RootState) => ({
    failoverDomain: state.failoverDomain.item,
    isLoading: state.app.loadingFlags.has(LOADING_FLAGS.FAILOVER_DOMAIN_ITEM),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    failoverDomainActions: bindActionCreators(failoverDomainActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FailoverDomainActivities));
